import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  AppBar,
  Toolbar,
  IconButton,
  Container,
  Menu,
  MenuItem,
  Chip,
  ListItemIcon,
  ListItemText,
  Divider,
  Popover,
  Box,
  Typography,
  Link,
} from "@mui/material";
import {
  ArrowBack as ArrowBackIcon,
  Home as HomeIcon,
  Search as SearchIcon,
  Notifications as NotificationsIcon,
  Campaign as CampaignIcon,
} from "@mui/icons-material";
import FiberManualRecordRoundedIcon from "@mui/icons-material/FiberManualRecordRounded";
import { useAuth } from "oidc-react";
import { format } from "date-fns";
import SearchModal from "./SearchModal";
import { useTeamMessages } from "./TeamMessagesProvider";
import { useClarityEventTracking } from "../hooks/useClarityTracking";
import { useTranslation } from "react-i18next";
import { getActiveThemeLogo } from "../theme/themeService";

const TEAM_MESSAGE_CONTENT_TRUNCATE_LIMIT = 250;

const Header = ({ hideButtons = false, handleBackButtonClick }) => {
  const { t } = useTranslation();
  const rootUrl = import.meta.env.VITE_BACKEND_API_ROOT_URL;
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [newNotifications, setNewNotifications] = useState(true);
  const [isSearchModalOpen, setSearchModalOpen] = useState(false);

  const sw = navigator.serviceWorker;

  const auth = useAuth();
  const accessToken = auth.userData?.access_token;

  const getNotifications = async () => {
    try {
      const API_BASE_URL = `${rootUrl}api/notifications`;
      const response = await fetch(API_BASE_URL, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setNotifications(data);
        setNewNotifications(false);
      }
    } catch (error) {
      console.error("Error fetching notification list:", error);
    }
  };

  // Marks a notification as read.
  // If woNumber exists -> treat as Work Order: call /api/notifications/wo/{woNumber}
  // If woNumber is missing -> treat as "other" type: call /api/notifications/other/{id}
  function markNotificationsRead(notification) {
    if (!notification) return Promise.resolve();
    const hasWo = Boolean(notification.woNumber);
    let url = null;
    if (hasWo && notification.woNumber) {
      url = `${rootUrl}api/notifications/wo/${notification.woNumber}`;
    } else if (notification.id) {
      url = `${rootUrl}api/notifications/other/${notification.id}`;
    } else {
      // No identifiers available; nothing to mark as read
      return Promise.resolve();
    }
    return fetch(url, {
      method: "DELETE",
      headers: { Authorization: `Bearer ${accessToken}` },
    });
  }

  // Mark all notifications as read (bulk)
  async function markAllNotificationsRead() {
    const unread = notifications.filter((n) => n.read === false);
    if (unread.length === 0) return;

    // Split into WO and "other"
    const woNos = unread
      .filter((n) => Boolean(n.woNumber))
      .map((n) => n.woNumber);

    const otherIds = unread
      .filter((n) => !n.woNumber && Boolean(n.id))
      .map((n) => n.id);

    // Set all to read locally
    setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));

    try {
      if (woNos.length > 0) {
        await fetch(`${rootUrl}api/notifications/wo`, {
          method: "DELETE",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${accessToken}`,
          },
          body: JSON.stringify(woNos),
        });
      }

      if (otherIds.length > 0) {
        await fetch(`${rootUrl}api/notifications/other`, {
          method: "DELETE",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${accessToken}`,
          },
          body: JSON.stringify(otherIds),
        });
      }
    } catch (e) {
      console.error("Failed to mark all notifications as read", e);
    }

    setNewNotifications(true);
    setAnchorEl(null);
  }

  useEffect(() => {
    if (newNotifications === true) {
      getNotifications();
    }
  }, [newNotifications]);

  useEffect(() => {
    if (sw) {
      window.addEventListener("load", () => {
        sw.register("/scripts/web-push/push-serviceWorker.js", {
          scope: "/scripts/web-push/",
        })
          .then((reg) => reg.ready)
          .then(() => {
            sw.addEventListener("message", ({ data }) => {
              if (data === "newNotification") {
                setNewNotifications(true);
              }
            });
          });
      });
    }
  }, [setNewNotifications, sw]);

  const {
    teamMessages,
    unreadCount: unreadTeamMessagesCount,
    markAllRead: markVisibleTeamMessagesRead,
    fetchIfStale: fetchTeamMessagesIfStale,
  } = useTeamMessages();

  const [teamMessagesAnchorEl, setTeamMessagesAnchorEl] = useState(null);
  const [expandedTeamMessageIds, setExpandedTeamMessageIds] = useState(
    () => new Set()
  );

  const handleTeamMessagesClick = (event) => {
    trackEvent("Visa teammeddelanden");
    if (teamMessagesAnchorEl) {
      setTeamMessagesAnchorEl(null);
      return;
    }
    setTeamMessagesAnchorEl(event.currentTarget);
    setExpandedTeamMessageIds(new Set());
    markVisibleTeamMessagesRead();
    fetchTeamMessagesIfStale();
  };

  const handleTeamMessagesClose = () => {
    setTeamMessagesAnchorEl(null);
  };

  const toggleTeamMessageExpanded = (id) => {
    setExpandedTeamMessageIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const teamMessagesOpen = Boolean(teamMessagesAnchorEl);

  const headerStyle = {
    backgroundColor: "#fff", // White background
    color: "#000", // Text color
    display: "flex",
    justifyContent: "center", // Center the header horizontally
  };

  const logoStyle = {
    flex: 1,
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
  };

  const logoImageStyle = {
    maxHeight: 50, // Max height of the logo set to 50px
  };

  const iconsStyle = {
    display: "flex",
    justifyContent: "flex-end",
  };

  const logoSrc = getActiveThemeLogo();

  const defaultHandleBackButtonClick = () => {
    navigate(-1);
  };

  const onBackButtonClick = () => {
    if (handleBackButtonClick) {
      handleBackButtonClick();
    } else {
      defaultHandleBackButtonClick();
    }
  };

  const handleHomeClick = () => {
    navigate("/");
  };

  const [anchorEl, setAnchorEl] = React.useState(null);

  const trackEvent = useClarityEventTracking();

  const handleNotificationsClick = (event) => {
    trackEvent("Visa notiser");
    setAnchorEl(anchorEl ? null : event.currentTarget);
  };

  const handleNotificationClick = async (notification) => {
    if (notification?.id) {
      setNotifications((prev) =>
        prev.map((n) => (n.id === notification.id ? { ...n, read: true } : n))
      );
    }

    try {
      await markNotificationsRead(notification);
    } catch (e) {
      console.error("Failed to mark notification as read", e);
    }

    setNewNotifications(true);
    setAnchorEl(null);

    // Navigate to task if woNumber exists
    if (notification?.woNumber) {
      navigate(`/task/${notification.woNumber}`);
      return;
    }

    // For notifications of type "other", if eventDate exists -> navigate to main page with date query param
    if (notification?.eventDate) {
      const dateObj = new Date(notification.eventDate);
      const dateParam = isNaN(dateObj.getTime())
        ? encodeURIComponent(String(notification.eventDate))
        : encodeURIComponent(format(dateObj, "yyyy-MM-dd"));
      navigate(`/?date=${dateParam}`);
      return;
    }
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);

  const handleSearchClick = () => {
    setSearchModalOpen(!isSearchModalOpen);
  };

  if (hideButtons) {
    return (
      <AppBar position="static" style={headerStyle}>
        <Container>
          <Toolbar>
            <div style={logoStyle}>
              <img alt="Logo" src={logoSrc} style={logoImageStyle} />
            </div>
          </Toolbar>
        </Container>
      </AppBar>
    );
  } else {
    return (
      <>
        <AppBar position="static" style={headerStyle}>
          <Container>
            <Toolbar>
              <IconButton
                edge="start"
                color="inherit"
                aria-label="back"
                onClick={onBackButtonClick}
              >
                <ArrowBackIcon />
              </IconButton>
              <div style={logoStyle}>
                <img alt="Logo" src={logoSrc} style={logoImageStyle} />
              </div>
              <div style={iconsStyle}>
                <IconButton
                  color="inherit"
                  aria-label={t("teamMessages.ariaLabel")}
                  onClick={handleTeamMessagesClick}
                >
                  <CampaignIcon />
                  {unreadTeamMessagesCount > 0 ? (
                    <Chip
                      label={unreadTeamMessagesCount}
                      color={"error"}
                      sx={{
                        height: 15,
                        width: 15,
                        fontSize: ".65rem",
                        marginLeft: "-8px",
                        marginTop: "10px",
                        "& .MuiChip-label": {
                          padding: 0,
                        },
                      }}
                    />
                  ) : (
                    <></>
                  )}
                </IconButton>
                <Popover
                  id="team-messages-menu"
                  anchorEl={teamMessagesAnchorEl}
                  open={teamMessagesOpen}
                  onClose={handleTeamMessagesClose}
                  anchorOrigin={{
                    vertical: "bottom",
                    horizontal: "left",
                  }}
                  transformOrigin={{
                    vertical: "top",
                    horizontal: "left",
                  }}
                  slotProps={{
                    paper: {
                      sx: {
                        width: "350px",
                        maxWidth: "100%",
                      },
                    },
                  }}
                >
                  {teamMessages.length === 0 ? (
                    <Box sx={{ p: 2 }}>
                      <Typography
                        variant="body2"
                        sx={{
                          color: "text.secondary",
                          fontStyle: "italic",
                        }}
                      >
                        {t("teamMessages.empty")}
                      </Typography>
                    </Box>
                  ) : (
                    teamMessages.map((message) => {
                      const content = message.content || "";
                      const isLong =
                        content.length > TEAM_MESSAGE_CONTENT_TRUNCATE_LIMIT;
                      const isExpanded = expandedTeamMessageIds.has(message.id);
                      const displayContent =
                        !isLong || isExpanded
                          ? content
                          : `${content
                              .slice(0, TEAM_MESSAGE_CONTENT_TRUNCATE_LIMIT)
                              .trimEnd()}…`;
                      return (
                        <Box
                          key={message.id}
                          sx={{
                            px: 2,
                            py: 1.5,
                            borderBottom: 1,
                            borderColor: "divider",
                            "&:last-of-type": { borderBottom: 0 },
                          }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "baseline",
                              gap: 1,
                            }}
                          >
                            <Typography
                              variant="subtitle2"
                              sx={{ fontWeight: 700 }}
                            >
                              {message.subject}
                            </Typography>
                            <Typography
                              variant="caption"
                              sx={{
                                color: "text.secondary",
                                flexShrink: 0,
                              }}
                            >
                              {format(new Date(message.created), "dd/M")}
                            </Typography>
                          </Box>
                          {message.teamName ? (
                            <Typography
                              variant="caption"
                              sx={{
                                display: "block",
                                color: "text.secondary",
                                mb: 0.5,
                              }}
                            >
                              {message.teamName}
                            </Typography>
                          ) : null}
                          <Typography
                            variant="body2"
                            sx={{
                              fontSize: "0.8rem",
                              fontWeight: 400,
                              whiteSpace: "pre-wrap",
                              mt: 0.5,
                            }}
                          >
                            {displayContent}
                          </Typography>
                          {isLong ? (
                            <Link
                              component="button"
                              type="button"
                              variant="caption"
                              underline="hover"
                              onClick={() =>
                                toggleTeamMessageExpanded(message.id)
                              }
                              sx={{ mt: 0.5 }}
                            >
                              {isExpanded
                                ? t("teamMessages.showLess")
                                : t("teamMessages.showMore")}
                            </Link>
                          ) : null}
                        </Box>
                      );
                    })
                  )}
                </Popover>
                <IconButton
                  color="inherit"
                  aria-label="Home"
                  onClick={handleHomeClick}
                >
                  <HomeIcon />
                </IconButton>
                <IconButton
                  color="inherit"
                  aria-label="Search"
                  onClick={handleSearchClick}
                >
                  <SearchIcon />
                </IconButton>
                <IconButton
                  color="inherit"
                  aria-label="Notifications"
                  onClick={handleNotificationsClick}
                >
                  <NotificationsIcon />
                  {notifications.filter((item) => item.read === false).length >
                  0 ? (
                    <Chip
                      label={
                        notifications.filter((item) => item.read === false)
                          .length
                      }
                      color={"error"}
                      sx={{
                        height: 15,
                        width: 15,
                        fontSize: ".65rem",
                        marginLeft: "-8px",
                        marginTop: "10px",
                        "& .MuiChip-label": {
                          padding: 0,
                        },
                      }}
                    />
                  ) : (
                    <></>
                  )}
                </IconButton>
                {notifications.length > 0 ? (
                  <Menu
                    id="notifications-menu"
                    anchorEl={anchorEl}
                    open={open}
                    onClose={handleClose}
                    anchorOrigin={{
                      vertical: "bottom",
                      horizontal: "left",
                    }}
                    transformOrigin={{
                      vertical: "top",
                      horizontal: "left",
                    }}
                    slotProps={{
                      paper: {
                        sx: {
                          width: "350px",
                          maxWidth: "100%",
                        },
                      },
                    }}
                  >
                    <MenuItem
                      onClick={markAllNotificationsRead}
                      disabled={
                        notifications.filter((item) => item.read === false)
                          .length === 0
                      }
                      sx={{
                        mx: 1,
                        my: 1,
                        borderRadius: 1,
                        fontWeight: 600,
                        bgcolor: "action.hover",
                        "&:hover": { bgcolor: "action.selected" },
                        "&.Mui-disabled": { opacity: 0.5 },
                      }}
                    >
                      <ListItemText
                        primary={t("workOrder.header.markAllAsRead")}
                      />
                    </MenuItem>

                    <Divider />

                    {notifications.map((row, key) => (
                      <MenuItem
                        onClick={() => handleNotificationClick(row)}
                        style={{ whiteSpace: "normal" }}
                        key={key}
                      >
                        <ListItemIcon>
                          <FiberManualRecordRoundedIcon
                            fontSize="small"
                            style={{
                              color: row.read ? "green" : "red",
                              paddingTop: "5px",
                            }}
                          />
                        </ListItemIcon>
                        <ListItemText>
                          <span
                            style={{ fontWeight: row.read ? "normal" : "bold" }}
                          >
                            {format(new Date(row.createdDate), "dd/M")}
                          </span>{" "}
                          {row.body}
                        </ListItemText>
                      </MenuItem>
                    ))}
                  </Menu>
                ) : (
                  <></>
                )}
              </div>
            </Toolbar>
          </Container>
        </AppBar>
        <SearchModal
          open={isSearchModalOpen}
          onClose={() => setSearchModalOpen(false)}
        />
      </>
    );
  }
};

export default Header;
